import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../utils/AuthContext";
import Spinner from "../../utils/Spinner/Spinner";
import styles from './LoginComponent.module.css';
import {getConfig, login, me} from "../../api/AuthApi";
import {loadRecaptcha} from "../../utils/CaptchaLoader/CaptchaLoader";

const LoginComponent: React.FC = () => {
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const {loggedIn, setLoggedIn, loading, setUserInfo} = useAuth();
    const navigate = useNavigate();
    const [siteKey, setSiteKey] = useState<string | null>(null);
    const [recaptchaReady, setRecaptchaReady] = useState<boolean>(false);

    useEffect(() => {
        if (!loading && loggedIn) {
            navigate("/");
        }
    }, [loggedIn, loading, navigate]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const key = await getConfig();
                if (cancelled) return;

                setSiteKey(key.siteKey);

                await loadRecaptcha(key.siteKey);
                if (cancelled) return;

                if (!window.grecaptcha?.ready) {
                    throw new Error("reCAPTCHA loaded but grecaptcha.ready missing");
                }

                window.grecaptcha.ready(() => {
                    if (!cancelled) setRecaptchaReady(true);
                });
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setRecaptchaReady(false);
                    setErrorMessage("reCAPTCHA failed to initialize. Please refresh and try again.");
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return <Spinner/>;
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!username.trim() || !password.trim()) {
            setErrorMessage("Both fields are required.");
            return;
        }

        if (!siteKey || !recaptchaReady) {
            setErrorMessage("reCAPTCHA is not ready yet. Please try again.");
            return;
        }

        try {
            const token: string = await new Promise((resolve, reject) => {
                window.grecaptcha!.ready(async () => {
                    try {
                        const t = await window.grecaptcha!.execute(siteKey, {action: "login"});
                        resolve(t);
                    } catch (err) {
                        reject(err);
                    }
                });
            });

            await login({username, password, recaptchaToken: token});

            setLoggedIn(true);
            const userInfo = await me();
            setUserInfo(userInfo);
            navigate("/");
        } catch (error) {
            console.error(error);
            setErrorMessage("Invalid credentials. Please try again.");
        }
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Login</h1>
            <form className={styles.form} onSubmit={handleSubmit} autoComplete="on">
                <input
                    className={styles.input}
                    type="text"
                    name="username"
                    autoComplete="username"
                    placeholder="E-mail or Tenant ID"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                />
                <input
                    className={styles.input}
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                />
                <button
                    className={styles.button}
                    type="submit"
                    disabled={!username.trim() || !password.trim() || !recaptchaReady}
                >
                    Login
                </button>
                {errorMessage && <p className={styles.error}>{errorMessage}</p>}
            </form>
        </div>
    );
};

export default LoginComponent;